/**
 * Phase 2b — pure scoring functions. No I/O, no side effects.
 *
 * Explainability contract: every returned component carries the raw
 * today+baseline inputs, so downstream consumers (the twin-ring card,
 * Jake's coach copy) can reconstruct "why" from stored data alone.
 *
 * Partial-data contract: a missing metric drops from the weight pool;
 * remaining weights are re-normalised across present signals. Missing
 * is never treated as zero.
 */

import {
  RECOVERY,
  RECOVERY_BANDS,
  RECOVERY_WEIGHTS,
  STRAIN_BANDS,
  STRAIN_WEIGHTS,
  type StrainCeilings,
} from './scoringConfig';

// ─── Types ─────────────────────────────────────────────────────────────────

export type RecoveryStage = 'insufficient' | 'calibrating' | 'ready';
export type RecoveryBand = 'low' | 'moderate' | 'high';
export type StrainBand = 'light' | 'moderate' | 'high';
export type RecoverySignal = 'sleep' | 'hrv' | 'rhr';
export type StrainSignal = 'lifting' | 'cardio' | 'steps';

export interface RecoveryTodayInputs {
  sleepMinutes: number | null;
  hrvSdnn: number | null;
  restingHr: number | null;
}

export interface RecoveryBaselines {
  sleepMinutes: number | null;
  hrvSdnn: number | null;
  restingHr: number | null;
}

export interface RecoveryComponent {
  sub: number; // 0-100 sub-score
  todayValue: number | null;
  baselineValue: number | null;
}

export interface RecoveryResult {
  score: number | null;
  stage: RecoveryStage;
  band: RecoveryBand | null;
  components: {
    sleep: RecoveryComponent;
    hrv: RecoveryComponent;
    rhr: RecoveryComponent;
  };
  signalsUsed: RecoverySignal[];
}

export interface StrainTodayInputs {
  liftingVolumeKg: number | null;
  cardioKcal: number | null;
  steps: number | null;
}

export interface StrainComponent {
  sub: number; // 0-100 sub-score
  raw: number | null;
}

export interface StrainResult {
  score: number | null;
  band: StrainBand | null;
  components: {
    lifting: StrainComponent;
    cardio: StrainComponent;
    steps: StrainComponent;
  };
  signalsUsed: StrainSignal[];
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const clamp = (n: number, lo = 0, hi = 100): number => Math.max(lo, Math.min(hi, n));

/** Linear interp: at `fullAt` → 100, at `zeroAt` → 0. Handles either direction. */
const piecewise = (value: number, zeroAt: number, fullAt: number): number => {
  if (fullAt === zeroAt) return value >= fullAt ? 100 : 0;
  const t = (value - zeroAt) / (fullAt - zeroAt);
  return clamp(t * 100);
};

// ─── Calibration ───────────────────────────────────────────────────────────

/**
 * Stage is driven by the MIN non-null day-count across the metrics the user
 * has actually shared. A user with only sleep+HRV can still reach 'ready'.
 * Metrics never shared (count 0) are excluded from the min.
 */
export const deriveRecoveryStage = (
  dayCounts: { sleep: number; hrv: number; rhr: number },
  thresholds: { min: number; ready: number },
): RecoveryStage => {
  const present = [dayCounts.sleep, dayCounts.hrv, dayCounts.rhr].filter((n) => n > 0);
  if (present.length === 0) return 'insufficient';
  const min = Math.min(...present);
  if (min < thresholds.min) return 'insufficient';
  if (min >= thresholds.ready) return 'ready';
  return 'calibrating';
};

// ─── Recovery ──────────────────────────────────────────────────────────────

const computeSleepSub = (todayMin: number, baselineMin: number): number => {
  const ratio = todayMin / baselineMin;
  let sub = piecewise(ratio, RECOVERY.SLEEP_ZERO_CREDIT_RATIO, RECOVERY.SLEEP_FULL_CREDIT_RATIO);
  if (todayMin < RECOVERY.SLEEP_ABSOLUTE_FLOOR_MIN) {
    sub = Math.min(sub, RECOVERY.SLEEP_ABSOLUTE_FLOOR_CAP);
  }
  return Math.round(sub);
};

const computeHrvSub = (todayMs: number, baselineMs: number): number => {
  const ratio = todayMs / baselineMs;
  return Math.round(
    piecewise(ratio, RECOVERY.HRV_ZERO_CREDIT_RATIO, RECOVERY.HRV_FULL_CREDIT_RATIO),
  );
};

const computeRhrSub = (todayBpm: number, baselineBpm: number): number => {
  // Lower is better: full credit at ratio ≤1, zero at ratio ≥1.15.
  const ratio = todayBpm / baselineBpm;
  return Math.round(
    piecewise(ratio, RECOVERY.RHR_ZERO_CREDIT_RATIO, RECOVERY.RHR_FULL_CREDIT_RATIO),
  );
};

export const computeRecovery = (
  today: RecoveryTodayInputs,
  baseline: RecoveryBaselines,
  stage: RecoveryStage,
): RecoveryResult => {
  const components = {
    sleep: {
      sub: 0,
      todayValue: today.sleepMinutes,
      baselineValue: baseline.sleepMinutes,
    } as RecoveryComponent,
    hrv: {
      sub: 0,
      todayValue: today.hrvSdnn,
      baselineValue: baseline.hrvSdnn,
    } as RecoveryComponent,
    rhr: {
      sub: 0,
      todayValue: today.restingHr,
      baselineValue: baseline.restingHr,
    } as RecoveryComponent,
  };

  if (stage === 'insufficient') {
    return { score: null, stage, band: null, components, signalsUsed: [] };
  }

  const signalsUsed: RecoverySignal[] = [];
  const weighted: Array<{ w: number; sub: number }> = [];

  if (today.sleepMinutes != null && baseline.sleepMinutes != null && baseline.sleepMinutes > 0) {
    components.sleep.sub = computeSleepSub(today.sleepMinutes, baseline.sleepMinutes);
    signalsUsed.push('sleep');
    weighted.push({ w: RECOVERY_WEIGHTS.sleep, sub: components.sleep.sub });
  }
  if (today.hrvSdnn != null && baseline.hrvSdnn != null && baseline.hrvSdnn > 0) {
    components.hrv.sub = computeHrvSub(today.hrvSdnn, baseline.hrvSdnn);
    signalsUsed.push('hrv');
    weighted.push({ w: RECOVERY_WEIGHTS.hrv, sub: components.hrv.sub });
  }
  if (today.restingHr != null && baseline.restingHr != null && baseline.restingHr > 0) {
    components.rhr.sub = computeRhrSub(today.restingHr, baseline.restingHr);
    signalsUsed.push('rhr');
    weighted.push({ w: RECOVERY_WEIGHTS.rhr, sub: components.rhr.sub });
  }

  if (weighted.length === 0) {
    return { score: null, stage, band: null, components, signalsUsed: [] };
  }

  // Redistribute weights across present signals.
  const wSum = weighted.reduce((a, x) => a + x.w, 0);
  const total = Math.round(weighted.reduce((a, x) => a + (x.w / wSum) * x.sub, 0));

  const band: RecoveryBand =
    total <= RECOVERY_BANDS.LOW_MAX
      ? 'low'
      : total <= RECOVERY_BANDS.MODERATE_MAX
        ? 'moderate'
        : 'high';

  return { score: total, stage, band, components, signalsUsed };
};

// ─── Strain ────────────────────────────────────────────────────────────────

const normaliseAgainstCeiling = (value: number, ceiling: number): number =>
  ceiling <= 0 ? 0 : Math.round(clamp((value / ceiling) * 100));

export const computeStrain = (
  today: StrainTodayInputs,
  ceilings: StrainCeilings,
): StrainResult => {
  const components = {
    lifting: { sub: 0, raw: today.liftingVolumeKg } as StrainComponent,
    cardio: { sub: 0, raw: today.cardioKcal } as StrainComponent,
    steps: { sub: 0, raw: today.steps } as StrainComponent,
  };

  const signalsUsed: StrainSignal[] = [];
  const weighted: Array<{ w: number; sub: number }> = [];

  // Lifting: available even if 0 (we know they logged nothing today only if
  // there's context that they *should* have; here we treat "no workouts on
  // this date" as no lifting signal to keep strain from being falsely low).
  if (today.liftingVolumeKg != null && today.liftingVolumeKg > 0) {
    components.lifting.sub = normaliseAgainstCeiling(
      today.liftingVolumeKg,
      ceilings.LIFTING_HARD_VOLUME_KG,
    );
    signalsUsed.push('lifting');
    weighted.push({ w: STRAIN_WEIGHTS.lifting, sub: components.lifting.sub });
  }

  if (today.cardioKcal != null && today.cardioKcal > 0) {
    components.cardio.sub = normaliseAgainstCeiling(
      today.cardioKcal,
      ceilings.CARDIO_BIG_DAY_KCAL,
    );
    signalsUsed.push('cardio');
    weighted.push({ w: STRAIN_WEIGHTS.cardio, sub: components.cardio.sub });
  }

  if (today.steps != null && today.steps > 0) {
    components.steps.sub = normaliseAgainstCeiling(today.steps, ceilings.STEPS_BIG_DAY);
    signalsUsed.push('steps');
    weighted.push({ w: STRAIN_WEIGHTS.steps, sub: components.steps.sub });
  }

  if (weighted.length === 0) {
    return { score: null, band: null, components, signalsUsed: [] };
  }

  const wSum = weighted.reduce((a, x) => a + x.w, 0);
  const total = Math.round(weighted.reduce((a, x) => a + (x.w / wSum) * x.sub, 0));

  const band: StrainBand =
    total <= STRAIN_BANDS.LIGHT_MAX
      ? 'light'
      : total <= STRAIN_BANDS.MODERATE_MAX
        ? 'moderate'
        : 'high';

  return { score: total, band, components, signalsUsed };
};

// ─── Baseline helpers ──────────────────────────────────────────────────────

/** Median of non-null numbers. Returns null when the input is empty. */
export const median = (values: Array<number | null | undefined>): number | null => {
  const nums = values.filter((v): v is number => v != null && Number.isFinite(v));
  if (nums.length === 0) return null;
  const sorted = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};
